import { useAppDispatch, useAppSelector } from 'features/app/hooks';
import {
  addPlusToPhoneNumber,
  parsePhoneNumber,
} from 'features/call/call-base/helpers';
import { AddHearingStatus } from 'features/call/call-details/enums';
import { setAddHearingStatus } from 'features/call/call-details/store';
import {
  setHearingLoadingStateByPhoneNumber,
  setCallDirection,
} from 'features/call/call-hearing/store';
import { ParticipantsStatusEventBus } from 'features/call/call-status/services';
import { callerSessionAnalyticsError } from 'features/caller-session/helpers';
import { checkShouldAllowNewHearingConnection } from 'features/caller-session/store';
import { was911Dialed } from 'features/emergency/helpers';
import { useEmergencySpawnNewCall } from 'features/emergency/hooks';
import { isVrsModeSelector } from 'features/multi-mode/store';
import { isSpecialPhoneNumber } from 'features/n11/helpers';
import { handleError } from 'features/notification/store';
import { handleVoiceMeetingError } from 'features/voice-meeting/helpers';
import {
  useVoiceMeetingSession,
  useVoiceMeetingSessionInit,
} from 'features/voice-meeting/hooks';
import { voiceSessionIdSelector } from 'features/voice-session/store';

export const useHearingConnect = () => {
  const dispatch = useAppDispatch();
  const voiceSessionId = useAppSelector(voiceSessionIdSelector);
  const { addParticipant } = useVoiceMeetingSession();
  const { spawnNewCallEmergency } = useEmergencySpawnNewCall();
  const { createNewVoiceSession, replaceN11Number, clearCallErrorIfExists } =
    useVoiceMeetingSessionInit();
  const isVrs = useAppSelector(isVrsModeSelector);

  const connectHearingToExistingSession = async (phoneNumber: string) => {
    let hearingPhoneNumber = phoneNumber;
    try {
      if (isVrs && isSpecialPhoneNumber(phoneNumber)) {
        hearingPhoneNumber =
          (await replaceN11Number(phoneNumber)) ?? phoneNumber;
        if (phoneNumber === hearingPhoneNumber) {
          dispatch(setAddHearingStatus(AddHearingStatus.ENTER_PHONE_NUMBER));
          return;
        }
      }
      clearCallErrorIfExists();
      const { shouldAllow, reasonMessage } = dispatch(
        checkShouldAllowNewHearingConnection(phoneNumber)
      );
      if (!shouldAllow) {
        dispatch(callerSessionAnalyticsError(reasonMessage));
        return;
      }
      ParticipantsStatusEventBus.hearing.$connecting.next(hearingPhoneNumber);
      const hearingParticipant = await addParticipant(
        addPlusToPhoneNumber(parsePhoneNumber(hearingPhoneNumber))
      );

      if (hearingParticipant) {
        ParticipantsStatusEventBus.hearing.$connectingId.next({
          phoneNumber: hearingPhoneNumber,
          id: hearingParticipant.id,
        });
      }
    } catch (error: unknown) {
      handleVoiceMeetingError(error);

      dispatch(
        handleError({
          error,
          methodName: 'connectHearingToExistingSession',
          disableUserNotification: true,
        })
      );
      ParticipantsStatusEventBus.hearing.$disconnected.next({
        phoneNumber: hearingPhoneNumber,
      });
    }
  };

  const connectHearing = async (phoneNumber: string) => {
    dispatch(
      setHearingLoadingStateByPhoneNumber({ isLoading: true, phoneNumber })
    );

    // When reconnecting to a call, ensure it's marked as outbound
    if (!voiceSessionId) {
      dispatch(setCallDirection('outbound'));
    }

    if (was911Dialed(phoneNumber) && isVrs) {
      await spawnNewCallEmergency();
    } else if (!voiceSessionId) {
      await createNewVoiceSession(phoneNumber);
    } else {
      await connectHearingToExistingSession(phoneNumber);
    }

    dispatch(
      setHearingLoadingStateByPhoneNumber({ isLoading: false, phoneNumber })
    );
  };

  return { connectHearing };
};
